import { Dispatch, ReactNode, SetStateAction, useState } from 'react'
import {
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  useTheme,
} from '@mui/material'

import { ETranslationNs } from 'src/types'

import { useStyles } from './styles'
import { HelperWrapper } from 'src/components/helper-wrapper'
import { useUpdateTranslationSwitch } from 'src/hooks/languages/use-update-translation-switch.hook'
import { ILoginTypeBaseWithId } from 'src/types/login-types.type'
import { GoogleIcon } from 'src/assets/icons/v2/GoogleIcon'
import { LinkedInIcon } from 'src/assets/icons/v2/LinkedInIcon'
import { XIcon } from 'src/assets/icons/v2/XIcon'
import { AppleIcon } from 'src/assets/icons/v2/AppleIcon'
import { MicrosoftIcon } from 'src/assets/icons/v2/MicrosoftIcon'
import { getTableTimestamp } from 'src/common/utils'
import { useMe } from 'src/hooks'
import { LockIcon } from 'src/assets/icons/v2/LockIcon'
import { useChangeLoginTypeStatus } from 'src/hooks/login-types/change-login-type-status.hook'
import { WalletIcon } from 'src/assets/icons/v2/WalletIcon'

interface IProps {
  items?: void | ILoginTypeBaseWithId[]
  sort: string
  setSort: Dispatch<SetStateAction<string>>
}

interface IIconObj {
  password: ReactNode
  google: ReactNode
  linkedin: ReactNode
  microsoft: ReactNode
  apple: ReactNode
  x: ReactNode
  web3: ReactNode
}

const LoginTypeTable = ({ items, sort, setSort }: IProps) => {
  const theme = useTheme()
  const classes = useStyles()
  const { data: user } = useMe()

  const { mutateAsync } = useChangeLoginTypeStatus()

  const isFunctionAllowed = (objects: ILoginTypeBaseWithId[]) => {
    let activeCount = 0
    for (let obj of objects) {
      if (obj.active) {
        activeCount++
      }
    }
    return activeCount > 1
  }

  const handleSortChange = (sortName: string) => () => {
    setSort((prevSort) =>
      prevSort.split('-')[1] === 'asc' ? `${sortName}-desc` : `${sortName}-asc`,
    )
  }
  const iconObj: IIconObj = {
    password: <LockIcon size={24} />,
    google: <GoogleIcon size={24} />,
    linkedin: <LinkedInIcon size={24} />,
    microsoft: <MicrosoftIcon size={24} />,
    apple: <AppleIcon size={24} />,
    x: <XIcon size={24} />,
    web3: <WalletIcon size={24}/>
  }
  const handleSwitchClick = (it: ILoginTypeBaseWithId) => {
    if ((items && isFunctionAllowed(items)) || !it.active) {
      mutateAsync(it.id)
    }
  }

  return (
    <>
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell align="center" width="10%" />
              <TableCell align="center" width="20%">
                <TableSortLabel
                  onClick={handleSortChange('name')}
                  active={sort.split('-')[0] === 'names'}
                  direction={sort.split('-')[1] as 'desc' | 'asc'}
                  sx={{
                    marginLeft: "20px"
                  }}
                >
                  <HelperWrapper t="impact-periods-name-column" />
                </TableSortLabel>
              </TableCell>
              <TableCell align="center" width="30%">
                <TableSortLabel
                  onClick={handleSortChange('created_at')}
                  active={sort.split('-')[0] === 'created_at'}
                  direction={sort.split('-')[1] as 'desc' | 'asc'}
                  sx={{
                    marginLeft: "20px"
                  }}
                >
                  <HelperWrapper t="table-created-column" />
                </TableSortLabel>
              </TableCell>
              <TableCell align="center" width="30%">
                <TableSortLabel
                  onClick={handleSortChange('updated_at')}
                  active={sort.split('-')[0] === 'updated_at'}
                  direction={sort.split('-')[1] as 'desc' | 'asc'}
                  sx={{
                    marginLeft: "20px"
                  }}
                >
                  <HelperWrapper t="table-modified-column" />
                </TableSortLabel>
              </TableCell>
              <TableCell align="center" width="10%">
                <HelperWrapper t="active-status-text" ns={ETranslationNs.Common} />
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {items?.map((item) => (
              <TableRow
                key={item.id}
                sx={{ 'td, th': { border: 0 } }}
                style={{
                  borderTop: `1px solid ${theme.palette.custom.grey}`,
                  borderBottom: `1px solid ${theme.palette.custom.grey}`,
                }}
              >
                <TableCell align="center" width="10%">
                  <div
                    style={{
                      backgroundColor: theme.palette.custom.primary,
                      height: 40,
                      width: 40,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      borderRadius: '50%',
                    }}
                  >
                    {iconObj[item.name as keyof IIconObj]}
                  </div>
                </TableCell>
                <TableCell align="center" width="20%">
                  {item.name.toLocaleUpperCase()}
                </TableCell>
                <TableCell align="center" width="30%">
                  {getTableTimestamp({ date: item.created_at, timezone: user?.timezone })}
                </TableCell>
                <TableCell align="center" width="30%">
                  {getTableTimestamp({ date: item.updated_at, timezone: user?.timezone })}
                </TableCell>
                <TableCell align="center" width="10%">
                  <Switch
                    size="small"
                    checked={item.active}
                    onChange={() => handleSwitchClick(item)}
                  />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </>
  )
}

export { LoginTypeTable }
